import easyCheckApi, { easyCheckRequest } from '../api/config';

// Payment Voucher Printing (optional module).
// All endpoints live under api/vouchers/* and are gated by the enableVoucherPrinting
// company preference (403 FEATURE_DISABLED when off). Printing a voucher never touches
// the cheque's status — this service is fully independent of chequeService.printSingleCheque.

const DEFAULT_DESIGN = {
  title: 'PAYMENT VOUCHER',
  headerLines: [''],
  headerAlign: 'right',
  showLogo: true,
  logoPosition: 'left',
  labelVoucherNo: 'No',
  labelDate: 'Date',
  labelPaidTo: 'Paid to Mr./M/s.',
  labelSumOf: 'The Sum of',
  labelChequeNo: 'Cheque No',
  labelBank: 'Bank',
  labelDated: 'Dated',
  labelBeing: 'Being',
  showChequeNo: true,
  showBank: true,
  showDated: true,
  showBeing: true,
  showAmountBox: true,
  amountBoxLabel: '',
  amountWordsStyle: 'upper',
  dateFormat: 'dd-MMM-yyyy',
  voucherDateSource: 'assigned',
  signatures: ['Prepared By', 'Approved By', "Receiver's Name and Signature"],
  footerNote: '',
};

const pick = (obj, pascal, camel, fallback) => {
  if (!obj) return fallback;
  const value = obj[pascal] ?? obj[camel];
  return value === undefined || value === null ? fallback : value;
};

const normalizeDesign = (d) => ({
  title: pick(d, 'Title', 'title', DEFAULT_DESIGN.title),
  headerLines: pick(d, 'HeaderLines', 'headerLines', DEFAULT_DESIGN.headerLines),
  headerAlign: pick(d, 'HeaderAlign', 'headerAlign', DEFAULT_DESIGN.headerAlign),
  showLogo: Boolean(pick(d, 'ShowLogo', 'showLogo', DEFAULT_DESIGN.showLogo)),
  logoPosition: pick(d, 'LogoPosition', 'logoPosition', DEFAULT_DESIGN.logoPosition),
  labelVoucherNo: pick(d, 'LabelVoucherNo', 'labelVoucherNo', DEFAULT_DESIGN.labelVoucherNo),
  labelDate: pick(d, 'LabelDate', 'labelDate', DEFAULT_DESIGN.labelDate),
  labelPaidTo: pick(d, 'LabelPaidTo', 'labelPaidTo', DEFAULT_DESIGN.labelPaidTo),
  labelSumOf: pick(d, 'LabelSumOf', 'labelSumOf', DEFAULT_DESIGN.labelSumOf),
  labelChequeNo: pick(d, 'LabelChequeNo', 'labelChequeNo', DEFAULT_DESIGN.labelChequeNo),
  labelBank: pick(d, 'LabelBank', 'labelBank', DEFAULT_DESIGN.labelBank),
  labelDated: pick(d, 'LabelDated', 'labelDated', DEFAULT_DESIGN.labelDated),
  labelBeing: pick(d, 'LabelBeing', 'labelBeing', DEFAULT_DESIGN.labelBeing),
  showChequeNo: Boolean(pick(d, 'ShowChequeNo', 'showChequeNo', DEFAULT_DESIGN.showChequeNo)),
  showBank: Boolean(pick(d, 'ShowBank', 'showBank', DEFAULT_DESIGN.showBank)),
  showDated: Boolean(pick(d, 'ShowDated', 'showDated', DEFAULT_DESIGN.showDated)),
  showBeing: Boolean(pick(d, 'ShowBeing', 'showBeing', DEFAULT_DESIGN.showBeing)),
  showAmountBox: Boolean(pick(d, 'ShowAmountBox', 'showAmountBox', DEFAULT_DESIGN.showAmountBox)),
  amountBoxLabel: pick(d, 'AmountBoxLabel', 'amountBoxLabel', DEFAULT_DESIGN.amountBoxLabel),
  amountWordsStyle: pick(d, 'AmountWordsStyle', 'amountWordsStyle', DEFAULT_DESIGN.amountWordsStyle),
  dateFormat: pick(d, 'DateFormat', 'dateFormat', DEFAULT_DESIGN.dateFormat),
  voucherDateSource: pick(d, 'VoucherDateSource', 'voucherDateSource', DEFAULT_DESIGN.voucherDateSource),
  signatures: pick(d, 'Signatures', 'signatures', DEFAULT_DESIGN.signatures),
  footerNote: pick(d, 'FooterNote', 'footerNote', DEFAULT_DESIGN.footerNote),
});

const normalizeSettings = (data) => ({
  prefix: pick(data, 'Prefix', 'prefix', 'PV-'),
  numberPadding: Number(pick(data, 'NumberPadding', 'numberPadding', 4)),
  nextNumber: Number(pick(data, 'NextNumber', 'nextNumber', 1)),
  hasIssuedVouchers: Boolean(pick(data, 'HasIssuedVouchers', 'hasIssuedVouchers', false)),
  lastIssuedNumber: pick(data, 'LastIssuedNumber', 'lastIssuedNumber', null),
  design: normalizeDesign(pick(data, 'Design', 'design', null)),
  logoBase64: pick(data, 'LogoBase64', 'logoBase64', null),
  logoContentType: pick(data, 'LogoContentType', 'logoContentType', null),
  updatedAt: pick(data, 'UpdatedAt', 'updatedAt', null),
  updatedBy: pick(data, 'UpdatedBy', 'updatedBy', null),
});

const unwrap = (response) => {
  if (!response.success) {
    throw new Error(response.message || 'Request failed');
  }
  let data = response.data;
  if (data && data.data) data = data.data;
  return data;
};

// Backend sends PascalCase `Message`/`ErrorCode`; the shared handleApiError only reads `message`
// and can swallow rule messages (e.g. VOUCHER_START_BELOW_USED), so surface them verbatim here.
const backendMessage = (error, fallback) => {
  const body = error?.response?.data;
  const message = body?.Message || body?.message || body?.ErrorDetails;
  if (message) return message;
  const status = error?.response?.status;
  if (status === 403) return 'Payment voucher printing is not enabled for your company';
  if (status === 401) return 'Your session has expired. Please sign in again.';
  return error?.message || fallback;
};

// Error responses for a blob request arrive as a Blob — decode to surface the backend Message verbatim.
const readBlobError = async (error) => {
  const fallback = 'Failed to generate the voucher PDF';
  try {
    const body = error?.response?.data;
    if (body instanceof Blob) {
      const text = await body.text();
      const json = JSON.parse(text);
      return json.Message || json.message || json.ErrorDetails || fallback;
    }
    if (body && typeof body === 'object') {
      return body.Message || body.message || fallback;
    }
  } catch (e) {
    // fall through
  }
  return backendMessage(error, fallback);
};

export const formatVoucherNumber = (prefix, number, padding) => {
  const digits = String(Math.max(0, Number(number) || 0));
  const padded = padding > 0 ? digits.padStart(padding, '0') : digits;
  return `${prefix || ''}${padded}`;
};

export const voucherService = {
  getDefaultDesign: () => ({ ...DEFAULT_DESIGN, headerLines: [...DEFAULT_DESIGN.headerLines], signatures: [...DEFAULT_DESIGN.signatures] }),

  /** GET api/vouchers/settings — numbering + design + logo (defaults when nothing saved yet). */
  getSettings: async () => {
    const response = await easyCheckRequest(() => easyCheckApi.get('/vouchers/settings'));
    return normalizeSettings(unwrap(response));
  },

  /**
   * PUT api/vouchers/settings — Admin only, preference must be on.
   * payload: { prefix, numberPadding, startingNumber, design, logoBase64?, removeLogo? }
   * Throws with the backend message (e.g. VOUCHER_START_BELOW_USED) on failure.
   */
  saveSettings: async (payload) => {
    try {
      const response = await easyCheckApi.put('/vouchers/settings', payload);
      const body = response.data || {};
      const success = body.Success ?? body.success ?? true;
      if (!success) throw new Error(body.Message || body.message || 'Failed to save voucher settings');
      return normalizeSettings(body.Data || body.data || body);
    } catch (error) {
      throw new Error(backendMessage(error, 'Failed to save voucher settings'));
    }
  },

  /**
   * GET api/vouchers/{chequeId}/pdf — opens the voucher in a new tab (download fallback when popups are blocked).
   * Assigns the voucher number on first print; reprints reuse it. Never changes cheque status.
   */
  printVoucher: async (chequeId) => {
    try {
      const response = await easyCheckApi.get(`/vouchers/${chequeId}/pdf`, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const newWindow = window.open(blobUrl, '_blank', 'width=1000,height=700,scrollbars=yes,resizable=yes');

      if (newWindow) {
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 5000);
      } else {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `voucher-${chequeId}.pdf`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }

      return { success: true, message: 'Voucher PDF opened in new tab' };
    } catch (error) {
      const message = await readBlobError(error);
      console.error('Voucher print failed:', message);
      return { success: false, message };
    }
  },
};

export default voucherService;
